import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Award, 
  Shield, 
  TrendingUp, 
  Target, 
  Globe, 
  Clock, 
  CheckCircle,
  Star,
  Zap,
  Lock,
  Heart
} from 'lucide-react';

const AboutPage = () => {
  const stats = [
    { value: '25+', label: 'Years Experience', icon: Clock },
    { value: '15,000+', label: 'Happy Clients', icon: Users },
    { value: '$2.8B', label: 'Assets Managed', icon: TrendingUp },
    { value: '94%', label: 'Success Rate', icon: Award }
  ];

  const values = [
    {
      icon: Zap,
      title: 'Strategic Innovation',
      description: 'We continuously evolve our investment strategies to capitalize on emerging opportunities while managing risk effectively.',
      color: 'blue'
    },
    {
      icon: Shield,
      title: 'Unwavering Integrity',
      description: 'We maintain the highest ethical standards in all our dealings, fostering transparency and trust with our clients.',
      color: 'green'
    },
    {
      icon: Heart,
      title: 'Client Partnership',
      description: 'We view each client relationship as a long-term partnership, committed to understanding your unique goals and aspirations.',
      color: 'purple'
    }
  ];

  const approach = [
    {
      step: '01',
      title: 'Comprehensive Analysis',
      description: 'We conduct thorough market analysis and due diligence to identify opportunities with optimal risk-adjusted returns.'
    },
    {
      step: '02',
      title: 'Strategic Diversification',
      description: 'We create portfolios with strategic asset allocation across multiple sectors, regions, and investment vehicles.'
    },
    {
      step: '03',
      title: 'Active Management',
      description: 'Our team continuously monitors and adjusts portfolios in response to changing market conditions and emerging opportunities.'
    },
    {
      step: '04',
      title: 'Performance Transparency',
      description: 'We provide clear, detailed reporting on portfolio performance and progress toward financial goals.'
    }
  ];

  const philosophy = [
    {
      title: 'Long-Term Perspective',
      description: 'We focus on sustainable growth rather than short-term fluctuations.',
      icon: Clock
    },
    {
      title: 'Risk Management',
      description: 'We prioritize capital preservation alongside growth opportunities.',
      icon: Shield
    },
    {
      title: 'Data-Driven Decisions',
      description: 'Our strategies are informed by rigorous research and analysis.',
      icon: TrendingUp
    },
    {
      title: 'Value Identification',
      description: 'We seek investments with intrinsic value beyond market pricing.',
      icon: Target
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <Navigation />

      {/* Hero Section - Simple & Clean */}
      <section className="relative pt-8 md:pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            {/* Simple Badge */}
            <div className="mb-8">
              <span className="inline-block bg-blue-600/20 px-6 py-3 rounded-full text-sm font-semibold text-blue-400 border border-blue-500/30">
                About Onchainvest Capital
              </span>
            </div>
            
            {/* Main Title */}
            <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
              <span className="text-white">Building Wealth</span>
              <br />
              <span className="text-blue-400">Since 1999</span>
            </h1>
            
            {/* Subtitle */}
            <p className="text-xl md:text-2xl mb-12 text-gray-300 leading-relaxed max-w-3xl mx-auto">
              We are a trusted investment partner helping individuals and families achieve their financial goals through strategic wealth management and innovative investment solutions.
            </p>
            
            {/* Simple CTA */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact">
                <button className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300">
                  Get In Touch
                </button>
              </Link>
              <Link to="/investment-plans">
                <button className="border-2 border-white/30 hover:bg-white/10 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300">
                  View Our Plans
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-700">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
                  <Icon className="mx-auto mb-4 text-blue-400" size={48} />
                  <div className="text-3xl font-bold mb-2">{stat.value}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Our Story Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Our Story
              </h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Founded in 1999, Onchainvest Capital emerged from a vision to transform how individuals and businesses approach wealth management. We recognized that traditional investment models often failed to provide personalized solutions that truly aligned with each client's unique financial goals and risk tolerance.
              </p>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Our name reflects our philosophy: linking disciplined strategy with blockchain-era opportunity—each client receives tailored investment approaches, not one-size-fits-all quotas.
              </p>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <div className="flex items-center">
                  <CheckCircle className="text-green-400 mr-2" size={16} />
                  <span>Licensed & Regulated</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="text-green-400 mr-2" size={16} />
                  <span>25+ Years Experience</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                  alt="Onchainvest Capital Office Building"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-xl shadow-2xl">
                <div className="text-white text-center">
                  <div className="text-2xl font-bold">1999</div>
                  <div className="text-xs opacity-90">Founded</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Mission & Values */}
      <section className="py-20 bg-gradient-to-r from-slate-700 to-slate-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Our Mission & Values
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              We're driven by core principles that guide our investment decisions and client relationships.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="card-glass p-8 rounded-2xl card-hover">
                  <div className={`w-12 h-12 bg-gradient-to-r from-${value.color}-600 to-${value.color}-500 rounded-full flex items-center justify-center mb-6`}>
                    <Icon className="h-6 w-6 text-white" />
              </div>
                  <h3 className="text-xl font-bold text-white mb-4">{value.title}</h3>
                  <p className="text-gray-300 leading-relaxed">
                    {value.description}
              </p>
            </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Our Approach */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Our Investment Approach
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                At Onchainvest Capital, we employ a disciplined investment methodology that balances proven strategies with innovative approaches tailored to each client's risk tolerance and objectives.
              </p>
              
              <div className="space-y-6">
                {approach.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {item.step}
                </div>
                  <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-gray-300">{item.description}</p>
                </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-glass p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-white mb-6">Investment Philosophy</h3>
              <p className="text-gray-300 mb-8 leading-relaxed">
                  Our investment decisions are guided by four fundamental principles that have consistently delivered value to our clients through various market cycles:
                </p>
                
              <div className="space-y-4">
                {philosophy.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="p-4 border-l-4 border-blue-500 bg-white/5 rounded-r-lg">
                      <div className="flex items-center mb-2">
                        <Icon className="text-blue-400 mr-3" size={20} />
                        <h4 className="font-semibold text-white">{item.title}</h4>
                      </div>
                      <p className="text-gray-300 text-sm ml-8">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Journey?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how Onchainvest Capital can help you achieve your financial objectives with strategies tailored to your unique situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-white text-slate-900 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl">
                Schedule a Consultation
              </button>
            </Link>
            <Link to="/investment-plans">
              <button className="border-2 border-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/10 transition-all duration-300">
                View Investment Plans
              </button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Certifications & Recognitions */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-700">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Recognition & Awards
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Onchainvest Capital is proud to be recognized by leading financial authorities and organizations.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-glass p-8 rounded-2xl text-center card-hover">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-bold text-white mb-4">Best Investment Firm 2023</h3>
              <p className="text-gray-300">Recognized by Financial Times for exceptional client service and performance.</p>
            </div>

            <div className="card-glass p-8 rounded-2xl text-center card-hover">
              <div className="text-4xl mb-4">⭐</div>
              <h3 className="text-xl font-bold text-white mb-4">5-Star Rating</h3>
              <p className="text-gray-300">Consistently rated 5-stars by our clients for transparency and results.</p>
            </div>

            <div className="card-glass p-8 rounded-2xl text-center card-hover">
              <div className="text-4xl mb-4">🔒</div>
              <h3 className="text-xl font-bold text-white mb-4">Security Excellence</h3>
              <p className="text-gray-300">Awarded for maintaining the highest security standards in the industry.</p>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default AboutPage;